import * as React from "react";
import {
    createContext,
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useContext,
    useState,
    useEffect,
    useRef
} from "react";
import { Feature } from "ol";
import { DrawType, OlDrawTypes } from "../Map/enums";
import { Collection } from "ol";
import { Draw, Modify, Select, DoubleClickZoom } from "ol/interaction";
import { getDrawingSymbol } from "../Map/symbols";
import { config } from "../Map/config";
import { getMap } from "../Forms/DrawingForm";
import { useRegistrationContext } from "./RegistrationContext";
import { primaryAction, singleClick } from "ol/events/condition";
import { Vector as VectorSource } from "ol/source";
import { useAppContext } from "./AppContext";
import VectorLayer from "ol/layer/Vector";
import { Type } from "ol/geom/Geometry";

const drawingLayerId = config.default.drawingLayerId;
export interface IDrawingContext {
    onDrawStart: (type: DrawType) => void;
    newFeature: Feature;
    setNewFeature: Dispatch<SetStateAction<Feature>>;
    onNoteChange: () => void;
    onDeleteFeature: (deletable: Feature) => void;
    selectedFeature: Feature;
    setSelectedFeature: Dispatch<SetStateAction<Feature>>;
    addFromSearch: (feature: Feature) => void;
    propertyNote: string;
    setPropertyNote: Dispatch<SetStateAction<string>>;
}

const DrawingContext = createContext<IDrawingContext>(null);

export const useDrawingContext = (): IDrawingContext => useContext(DrawingContext);

export const DrawingContextProvider: FC = (props: React.PropsWithChildren<IDrawingContext>) => {
    const { isMobile, scaleWarning, drawType, setDrawType, setIdentifiedFeatures } = useAppContext();
    const { drawnFeatures, setDrawnFeatures, setDrawingMenuVisible } = useRegistrationContext();

    const [newFeature, setNewFeature] = useState<Feature>();
    const [selectedFeature, setSelectedFeature] = useState<Feature>();

    const propertyFeature = drawnFeatures && drawnFeatures.find((f) => f.get("DRAWTYPE") === DrawType.PolygonProperty);
    const [propertyNote, setPropertyNote] = useState<string>(propertyFeature && propertyFeature.get("MASTERNAME"));

    const drawRef = useRef<Draw>();
    const selectRef = useRef<Select>();
    const modifyRef = useRef<Modify>();
    const drawSourceRef = useRef<VectorSource>();
    const selectCollectionRef = useRef<Collection<Feature>>(new Collection());

    const drawTypeRef = useRef<DrawType>(drawType);
    const scaleWarningRef = useRef<boolean>(scaleWarning);
    const selectedFeatureRef = useRef<Feature>(selectedFeature);

    useEffect(() => {
        scaleWarningRef.current = scaleWarning;
    }, [scaleWarning]);

    useEffect(() => {
        selectedFeatureRef.current = selectedFeature;

        if (selectCollectionRef.current) {
            selectCollectionRef.current.clear();
            selectedFeature && selectCollectionRef.current.push(selectedFeature);
        }
    }, [selectedFeature]);

    useEffect(() => {
        !drawSourceRef.current && (drawSourceRef.current = getDrawingLayer().getSource());
        !selectRef.current && addSelectInteraction();
        !modifyRef.current && addModifyInteraction();
    }, []);

    useEffect(() => {
        if (!newFeature) {
            return;
        }
        newFeature.setProperties({
            TEMPID: Date.now(),
            DESCRIPTION: "",
            DRAWTYPE: newFeature.get("DRAWTYPE") || drawType
        });
        setDrawnFeatures([...drawnFeatures, newFeature]);
        setNewFeature(null);
        setDrawType(undefined);
        drawTypeRef.current = undefined;
        setDrawingMenuVisible(true);
    }, [drawnFeatures, newFeature]);

    useEffect(() => {
        setSelectedFeature(null);
        selectCollectionRef.current.clear();
        drawType && (drawType === DrawType.PointYard || drawType === DrawType.PolylineRoad)
            ? !drawRef.current && addDrawInteraction()
            : removeDrawInteraction();
    }, [drawType]);

    const onDrawStart = (type: DrawType) => {
        if (type === drawTypeRef.current) {
            // cancel current mode, if click again on draw button
            removeDrawInteraction();
            setDrawType(undefined);
            drawTypeRef.current = undefined;
            setDoubleClickZoom(true);
            setDrawingMenuVisible(true);
            return;
        }
        drawTypeRef.current && drawTypeRef.current !== type && removeDrawInteraction();
        setDrawType(type);
        drawTypeRef.current = type;
        isMobile && setDrawingMenuVisible(false);
        setDoubleClickZoom(false);
    };

    const onNoteChange = useCallback(() => {
        setDrawnFeatures(drawnFeatures);
        const propertyFeature = drawnFeatures.find((f) => f.get("DRAWTYPE") === DrawType.PolygonProperty);
        propertyFeature && setPropertyNote(propertyFeature.get("MASTERNAME"));
    }, [drawnFeatures]);

    const onDeleteFeature = useCallback(
        (deletable) => {
            deletable.get("DRAWTYPE") === DrawType.PolygonProperty && setPropertyNote(undefined);
            const features = drawnFeatures.filter((f) => f.get("TEMPID") !== deletable.get("TEMPID"));
            setDrawnFeatures(features);
            getDrawingLayer().getSource().removeFeature(deletable);
            setSelectedFeature(null);
            selectCollectionRef.current.clear();
        },
        [drawnFeatures]
    );

    const addDrawInteraction = () => {
        drawRef.current = new Draw({
            condition: (event) => primaryAction(event) && !scaleWarningRef.current,
            source: drawSourceRef.current,
            type: getDrawType(drawType),
            stopClick: true
        });
        getMap().addInteraction(drawRef.current);
        drawSourceRef.current.once("addfeature", (e) => {
            removeDrawInteraction();
            setNewFeature(e.feature);

            setTimeout(() => setDoubleClickZoom(true), 260); // doubleClick lasts 250ms
        });
    };

    const removeDrawInteraction = () => {
        getMap().removeInteraction(drawRef.current);
        drawRef.current = null;
    };

    const addSelectInteraction = () => {
        selectRef.current = new Select({
            features: selectCollectionRef.current,
            layers: [getDrawingLayer()],
            hitTolerance: 20,
            style: (feature) => getDrawingSymbol(feature.get("DRAWTYPE"), feature.get("MASTERNAME"), true)
        });
        selectRef.current.on("select", onSelect);
        getMap().addInteraction(selectRef.current);
    };

    const onSelect = (e) => {
        if (e.selected[0]) {
            setSelectedFeature(e.selected[0]);
            return;
        }
        selectCollectionRef.current.clear();
        setSelectedFeature(null);
    };

    const addModifyInteraction = () => {
        modifyRef.current = new Modify({
            features: selectCollectionRef.current,
            condition: (event) =>
                primaryAction(event) &&
                selectedFeatureRef.current &&
                selectedFeatureRef.current.get("DRAWTYPE") !== DrawType.PolylineLvmRoad &&
                selectedFeatureRef.current.get("DRAWTYPE") !== DrawType.PolygonProperty,
            pixelTolerance: 15,
            style: (feature) =>
                getDrawingSymbol(
                    feature.get("features")[0].get("DRAWTYPE"),
                    feature.get("features")[0].get("MASTERNAME"),
                    true,
                    true
                )
        });
        modifyRef.current.on("modifyend", onModifyFeature);
        getMap().addInteraction(modifyRef.current);
    };

    const onModifyFeature = () => setDrawnFeatures(getDrawingLayer().getSource().getFeatures());

    const addFromSearch = (feature: Feature) => {
        const source = getDrawingLayer().getSource();

        const alreadyAdded = source
            .getFeatures()
            .find(
                (f) => f.get("DRAWTYPE") === feature.get("DRAWTYPE") && f.get("MASTERID") === feature.get("MASTERID")
            );

        if (alreadyAdded) {
            return;
        }

        !feature.get("DRAWTYPE") &&
            feature.set("DRAWTYPE", feature.get("CODE") ? DrawType.PolygonProperty : DrawType.PointYard);

        source.addFeature(feature);
        setNewFeature(feature);
        setIdentifiedFeatures(undefined);
    };

    const values = {
        onDrawStart,
        newFeature,
        setNewFeature,
        onNoteChange,
        onDeleteFeature,
        selectedFeature,
        setSelectedFeature,
        addFromSearch,
        propertyNote,
        setPropertyNote
    };

    return <DrawingContext.Provider value={values}>{props.children}</DrawingContext.Provider>;
};

const getDrawingLayer = () =>
    getMap()
        .getLayers()
        .getArray()
        .find((l) => l.get("id") === drawingLayerId) as VectorLayer<VectorSource>;

const getDrawType = (type: DrawType): Type =>
    type === DrawType.PolygonProperty
        ? OlDrawTypes.Polygon
        : type === DrawType.PolylineRoad || type === DrawType.PolylineLvmRoad
        ? OlDrawTypes.LineString
        : OlDrawTypes.Point;

const setDoubleClickZoom = (active: boolean) => {
    const interactions = getMap().getInteractions();
    for (let i = 0; i < interactions.getLength(); i++) {
        const interaction = interactions.item(i);
        if (interaction instanceof DoubleClickZoom) {
            interaction.setActive(active);
            break;
        }
    }
};
